import React, { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { useTheme } from '../context/ThemeContext';
import { Mic, MessageSquare, Radar, Phone, RefreshCw, Lightbulb, Loader2 } from 'lucide-react';

const usd = (v, d = 3) => {
  if (v == null) return '—';
  const t = (Math.round(Number(v) * 10 ** d + 1e-9) / 10 ** d).toFixed(d);
  return `$${t.replace(/0+$/, '').replace(/\.(\d)?$/, (m, x) => `.${(x || '') + '0'.repeat(2 - (x || '').length)}`)}`;
};
const VOICE_NAMES = { eleven_v4_turbo: 'Eleven v4 Turbo', eleven_v4: 'Eleven v4', eleven_flash_v2_5: 'Flash v2.5', eleven_turbo_v2_5: 'Turbo v2.5', eleven_v3: 'v3 Conversational', eleven_multilingual_v2: 'Multilingual v2' };
const TABS = [['voice', 'Voice calls', Mic], ['chat', 'AI chat', MessageSquare], ['growth', 'Growth tools', Radar], ['phone', 'Phone & keys', Phone]];

const useStyles = (dark) => ({
  card: `rounded-2xl border p-5 ${dark ? 'border-white/[0.08] bg-white/[0.025]' : 'border-slate-200 bg-slate-50/60'}`,
  muted: dark ? 'text-white/50' : 'text-slate-500',
  strong: dark ? 'text-white' : 'text-slate-900',
  line: dark ? 'border-white/[0.06]' : 'border-slate-200',
});

const Section = ({ title, hint, children, s, testid }) => (
  <section className={s.card} data-testid={testid}>
    <h3 className={`text-sm font-bold ${s.strong}`}>{title}</h3>
    {hint && <p className={`text-xs mt-1 leading-relaxed ${s.muted}`}>{hint}</p>}
    <div className="mt-3">{children}</div>
  </section>
);

const Row = ({ label, sub, value, unit, s, id }) => (
  <div className={`flex items-center justify-between gap-4 py-3 border-t first:border-t-0 ${s.line}`} data-testid={`usage-rate-${id}`}>
    <div className="min-w-0"><p className={`text-sm ${s.strong}`}>{label}</p>{sub && <p className={`text-[11px] ${s.muted}`}>{sub}</p>}</div>
    <p className={`text-sm font-bold tabular-nums whitespace-nowrap ${s.strong}`}>{value}{unit && <span className={`ml-1 text-[11px] font-normal ${s.muted}`}>/{unit}</span>}</p>
  </div>
);

const Table = ({ head, rows, s, testid }) => (
  <table className="w-full text-xs sm:text-sm" data-testid={testid}>
    <thead><tr className={`text-[11px] uppercase tracking-wider ${s.muted}`}>{head.map((h, i) => <th key={h} className={`pb-2 font-semibold ${i ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr></thead>
    <tbody>{rows.map((r, ri) => <tr key={ri} className={`border-t ${s.line}`}>{r.map((c, i) => <td key={i} className={`py-2.5 tabular-nums ${i ? 'text-right' : ''} ${i === 0 ? s.strong : ''}`}>{c}</td>)}</tr>)}</tbody>
  </table>
);

const Tip = ({ children, s }) => <p className={`flex gap-2 text-xs leading-relaxed ${s.muted}`}><Lightbulb size={14} className="text-teal-400 shrink-0 mt-0.5" />{children}</p>;

const VoiceTab = ({ d, s }) => {
  const voice = d.rates.voice || {};
  const models = d.voice_ai_models || [];
  const a = d.admin_costs;
  const retail = a ? a.retail_voice : voice;
  const base = retail.eleven_v4_turbo;
  const pick = m => models.find(x => x.model === m);
  const examples = ['claude-sonnet-5-5', 'claude-opus-5-5'].map(pick).filter(Boolean);
  const mk = 1 + (d.voice_ai_markup || 0);
  const clientMk = a ? 1.15 : mk;
  return <div className="space-y-4" data-testid="rates-voice-content">
    <Section s={s} title="1 · Voice" hint="Charged per minute of conversation, based on the voice model you pick." testid="rates-voice-engines">
      {Object.entries(retail).map(([k, v]) => <Row key={k} id={k} s={s} label={VOICE_NAMES[k] || k} value={usd(v, 3)} unit="min" />)}
    </Section>
    <Section s={s} title="2 · AI model" hint={`What the AI "brain" costs per minute — provider cost + ${Math.round((clientMk - 1) * 100)}%. Longer prompts cost more every minute.`} testid="rates-voice-ai-models">
      {models.length ? <Table s={s} testid="rates-voice-ai-table" head={['AI model', '~5k-char prompt', '~12k-char prompt']}
        rows={models.map(m => [m.label, `${usd(m.short * clientMk)}/min`, `${usd(m.long * clientMk)}/min`])} />
        : <p className={`text-xs ${s.muted}`}>Live prices unavailable right now. Try again shortly.</p>}
    </Section>
    {base != null && examples.length > 0 && <Section s={s} title="Typical total per minute" hint={`What a client pays: ${VOICE_NAMES.eleven_v4_turbo} voice + AI model with a typical ~5k-character prompt.`} testid="rates-voice-total">
      <div className="grid sm:grid-cols-2 gap-3">
        {examples.map(m => <div key={m.model} className={`rounded-xl border p-4 ${s.line}`} data-testid={`rates-total-${m.model}`}>
          <p className={`text-xs ${s.muted}`}>{m.label}</p>
          <p className="text-2xl font-black text-teal-400 mt-1">≈ {usd(base + m.short * clientMk, 2)}<span className={`text-xs font-normal ml-1 ${s.muted}`}>/min</span></p>
          <p className={`text-[11px] mt-1 ${s.muted}`}>{usd(base, 2)} voice + {usd(m.short * clientMk)} AI</p>
        </div>)}
      </div>
      <p className={`text-[11px] mt-3 ${s.muted}`}>Phone line minutes are billed separately by your carrier (Twilio US ≈ $0.015/min).</p>
    </Section>}
    <Tip s={s}>Sonnet 5.5 is the best value for most agents. Opus 5.5 costs about twice as much per minute. Trimming a long prompt roughly halves the AI cost.</Tip>
  </div>;
};

const ChatTab = ({ d, s }) => {
  const chat = d.rates.chat || {};
  const vals = [...new Set(Object.values(chat))];
  return <div className="space-y-4" data-testid="rates-conversations-content">
    <Section s={s} title="AI replies" hint="One simple price for every AI model and every channel — SMS, web chat, Facebook, Instagram, WhatsApp." testid="rates-chat-flat">
      {vals.length === 1 ? <Row id="chat-flat" s={s} label="Every AI reply, any model" value={usd(vals[0], 3)} unit="reply" />
        : Object.entries(chat).map(([k, v]) => <Row key={k} id={`chat-${k}`} s={s} label={k} value={usd(v, 3)} unit="reply" />)}
      <Row id="chat-byok" s={s} label="Using your own OpenAI / Claude key" sub="Your provider bills the AI tokens directly" value={usd(d.byok_fee_per_msg, 3)} unit="reply" />
      <Row id="kb" s={s} label="Knowledge base lookup" value={usd(d.rates.kb, 3)} unit="session" />
    </Section>
  </div>;
};

const GrowthTab = ({ d, s }) => {
  return <div className="space-y-4" data-testid="rates-growth-content">
    <Section s={s} title="Deep Analysis & Proposal" hint="Per completed business: website analysis, brand profile, AI demo agent, knowledge base and proposal. Finding leads is free. Failed businesses are never charged." testid="rates-lead">
      <Row id="lead-package" s={s} label="Platform AI" value={usd(d.lead_package_fee, 2)} unit="business" />
      <Row id="lead-byok" s={s} label="Using your own Claude key" sub="Platform fee only — your provider bills the tokens" value={usd(d.lead_package_byok_fee, 2)} unit="business" />
    </Section>
    <Section s={s} title="Brand Profile" hint="Standalone website analysis. Included free inside Deep Analysis." testid="rates-brand">
      <Row id="brand-standard" s={s} label="Website analysis" value={usd(d.website_analysis_fee, 2)} unit="analysis" />
      <Row id="brand-byok" s={s} label="Using your own key" value={usd(d.website_analysis_byok_fee, 2)} unit="analysis" />
    </Section>
    <Section s={s} title="AI Architect" hint="Charged only when a build completes." testid="rates-architect">
      <Row id="architect-standard" s={s} label="Agent build" value={usd(d.ai_architect_fee, 2)} unit="build" />
      <Row id="architect-byok" s={s} label="Using your own key" value={usd(d.ai_architect_byok_fee, 2)} unit="build" />
    </Section>
    <Section s={s} title="Team alerts" testid="rates-channels">
      <Row id="channel-trigger" s={s} label="Pumble / Slack / Zapier delivery" value={usd(d.messaging_trigger_fee, 2)} unit="alert" />
    </Section>
  </div>;
};

const PhoneTab = ({ s }) => <div className="space-y-4" data-testid="rates-provider-content">
  <Section s={s} title="Twilio" hint="Phone numbers, call minutes and carrier text fees are billed directly by Twilio. Voicemail detection adds about $0.0075 per call." />
  <Section s={s} title="Your own API keys" hint="When you connect Anthropic, OpenAI or Google, their charges appear on your provider account. Voxinity only adds the small platform fee shown in each tab." />
</div>;

const UsageRatesModal = ({ api, workspaceId, onClose }) => {
  const { isDarkMode: dark } = useTheme();
  const s = useStyles(dark);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState('voice');
  const load = async () => { setError(false); try { setData((await api.get(`/api/workspaces/${workspaceId}/credits/rates`)).data); } catch { setError(true); } };
  useEffect(() => { load(); }, [workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps
  const Body = { voice: VoiceTab, chat: ChatTab, growth: GrowthTab, phone: PhoneTab }[tab];
  return <Sheet open onOpenChange={v => { if (!v) onClose(); }}>
    <SheetContent side="right" data-testid="usage-rates-modal"
      className={`w-full sm:max-w-none sm:w-[53vw] sm:min-w-[600px] p-0 flex flex-col ${dark ? 'bg-[#0d0f14] border-white/10 text-white' : 'bg-white'}`}>
      <SheetHeader className={`px-6 sm:px-8 pt-7 pb-4 border-b text-left ${s.line}`}>
        <SheetTitle className={`text-2xl font-display font-black ${s.strong}`}>Usage &amp; Billing Rates</SheetTitle>
        <SheetDescription className={s.muted}>Pay only for what you use. All prices in USD.</SheetDescription>
        <div className={`mt-4 flex flex-wrap w-fit p-1 rounded-xl gap-1 ${dark ? 'bg-white/[0.04]' : 'bg-slate-100'}`} role="tablist">
          {TABS.map(([id, label, Icon]) => <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`rates-tab-${id}`}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${tab === id ? 'bg-teal-400/15 text-teal-300' : `${s.muted} hover:text-teal-300`}`}>
            <Icon size={13} />{label}</button>)}
        </div>
      </SheetHeader>
      <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6">
        {!data && !error && <p className={`flex items-center gap-2 text-sm ${s.muted}`} data-testid="usage-rates-loading"><Loader2 size={14} className="animate-spin" /> Loading rates…</p>}
        {error && <div role="alert" className="text-sm text-red-400 flex items-center gap-2" data-testid="usage-rates-error">Could not load current rates.
          <button data-testid="usage-rates-retry" onClick={load} title="Retry"><RefreshCw size={14} /></button></div>}
        {data && <Body d={data} s={s} />}
      </div>
      <div className={`px-6 sm:px-8 py-4 border-t flex items-center justify-between ${s.line}`}>
        <p className={`text-[11px] ${s.muted}`}>Rates for your workspace · USD</p>
        <button onClick={onClose} data-testid="usage-rates-modal-close" className="px-5 py-2.5 rounded-xl bg-teal-400 text-black text-sm font-bold hover:bg-teal-300 transition-colors">Done</button>
      </div>
    </SheetContent>
  </Sheet>;
};
export default UsageRatesModal;
